'use client';

import {
  NodeDetailsPopover,
  type RepositoryInfo,
} from '@terra/ui-design-system';
import {
  type Edge,
  type Node,
  type ReactFlowInstance,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { GraphNodeType } from './types';
import { GRAPH_COLORS } from './types';
import type {
  CodeGraphWithRelations,
  CodeGraphNode,
  CodeGraphEdge,
} from '@terra/shared-schemas-git';
import { GraphFilters } from './GraphFilters';
import { Box, useBreakpointValue } from '@terra/ui-styled-system';
import type { CustomNodeProps } from './CustomNode';
import { CustomNode } from './CustomNode';
import { CustomEdge } from './CustomEdge';
import { getNodeColor, getNodeId } from './utils';
import {
  getEdgeSelectionOpacity,
  getNodeSelectionOpacity,
} from '../flow-graph-base/graph.utils';
import { GraphFiltersProvider } from './GraphFiltersContext';
import { NODE_DEFAULT_HEIGHT, NODE_DEFAULT_WIDTH } from './constants';
import { FlowGraphBase } from '../flow-graph-base/FlowGraphBase';

export type CodeGraphNodeProps = Node<CustomNodeProps>;

export interface CodeGraphProps {
  data: CodeGraphWithRelations;
  repositoryInfoMap: Record<string, RepositoryInfo>;
  width?: number;
  height?: number;
  onNodeClick?: (node: CodeGraphNodeProps) => void;
}

export const CodeGraph = ({
  data,
  repositoryInfoMap,
  width,
  height,
  onNodeClick,
}: CodeGraphProps) => {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const flowRef = useRef<ReactFlowInstance | null>(null);
  const [selectedNode, setSelectedNode] = useState<CodeGraphNodeProps | null>(
    null,
  );
  const graphWrapperRef = useRef<HTMLDivElement>(null);
  const nodeDetailsPopoverRef = useRef<HTMLDivElement>(null);
  const nodePopoverSpacing = useBreakpointValue({ md: 30, lg: 100 });

  const nodeTypes = useMemo(() => ({ custom: CustomNode }), []);
  const edgeTypes = useMemo(() => ({ custom: CustomEdge }), []);

  useEffect(() => {
    if (
      !data ||
      !data.nodes ||
      !data.edges ||
      !Array.isArray(data.nodes) ||
      !Array.isArray(data.edges)
    ) {
      setNodes([]);
      setEdges([]);
      return;
    }

    const flowNodes = data.nodes.map((node: CodeGraphNode) => {
      const nodeData: CustomNodeProps = {
        type: node.type as GraphNodeType,
        name: node.name,
        filePath: node.filePath,
        repositoryName: node.repositoryName,
        startLine: node.startLine ?? undefined,
        endLine: node.endLine ?? undefined,
        repositoryInfo: undefined,
        technologies: data.technologies?.filter((technology) =>
          technology.relatedNodeIds?.includes(node.id),
        ),
        id: getNodeId(node.id),
      };

      return {
        id: node.id,
        type: 'custom',
        position: { x: 0, y: 0 },
        data: nodeData,
      } as Node;
    });

    const flowEdges = data.edges.map(
      (edge: CodeGraphEdge) =>
        ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          label: edge.label,
          type: 'custom',
        }) as Edge,
    );

    setNodes(flowNodes);
    setEdges(flowEdges);
  }, [data, setEdges, setNodes]);

  useEffect(() => {
    setNodes((currentNodes) =>
      currentNodes.map((node) => {
        const nodeData = node.data as CustomNodeProps;
        return {
          ...node,
          data: {
            ...nodeData,
            repositoryInfo: nodeData.repositoryName
              ? repositoryInfoMap[nodeData.repositoryName]
              : undefined,
          },
        };
      }),
    );
  }, [repositoryInfoMap, setNodes]);

  useEffect(() => {
    setNodes((currentNodes) =>
      currentNodes.map((node) => ({
        ...node,
        style: {
          ...node.style,
          opacity: getNodeSelectionOpacity(node.id, selectedNode?.id, 0.1),
          transition: 'opacity 0.3s ease',
        },
      })),
    );
    setEdges((currentEdges) =>
      currentEdges.map((edge) => ({
        ...edge,
        style: {
          ...edge.style,
          opacity: getEdgeSelectionOpacity(
            edge.source,
            edge.target,
            selectedNode?.id,
            0.1,
          ),
          transition: 'opacity 0.3s ease',
        },
      })),
    );
  }, [selectedNode, setNodes, setEdges]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (
        !selectedNode ||
        !flowRef.current ||
        !nodeDetailsPopoverRef.current ||
        !graphWrapperRef.current
      ) {
        return;
      }

      const popoverWidth = nodeDetailsPopoverRef.current.offsetWidth;
      const parentRect = graphWrapperRef.current.getBoundingClientRect();
      const popoverRect = nodeDetailsPopoverRef.current.getBoundingClientRect();

      const popoverRightOffset = parentRect.right - popoverRect.right;
      const viewportWidth = graphWrapperRef.current.offsetWidth;
      const popoverLeftScreenX =
        viewportWidth - popoverWidth - popoverRightOffset;

      const nodeWidth =
        selectedNode.width ??
        selectedNode.measured?.width ??
        NODE_DEFAULT_WIDTH;
      const nodeHeight =
        selectedNode.height ??
        selectedNode.measured?.height ??
        NODE_DEFAULT_HEIGHT;
      const nodeCenterFlowX = selectedNode.position.x + nodeWidth / 2;
      const targetNodeScreenX =
        popoverLeftScreenX - nodeWidth / 2 - nodePopoverSpacing;

      const screenOffsetX = targetNodeScreenX - viewportWidth / 2;

      const targetCenterFlowX = nodeCenterFlowX - screenOffsetX;
      const targetCenterFlowY = selectedNode.position.y + nodeHeight / 2;

      flowRef.current.setCenter(targetCenterFlowX, targetCenterFlowY, {
        duration: 800,
        zoom: 1,
      });
    }, 100);

    return () => clearTimeout(timeout);
  }, [selectedNode, nodePopoverSpacing]);

  const onGetNodeColor = useCallback((node: Node) => {
    const nodeData = node.data as CustomNodeProps;
    return getNodeColor(nodeData.type);
  }, []);

  const handleClosePopover = useCallback(() => {
    setSelectedNode(null);
  }, []);

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (typeof onNodeClick === 'function') {
        onNodeClick(node as CodeGraphNodeProps);
      }
      setSelectedNode(node as CodeGraphNodeProps);
    },
    [onNodeClick],
  );

  const handleNodeDrag = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      if (node.id !== selectedNode?.id) {
        handleClosePopover();
      }
    },
    [selectedNode?.id, handleClosePopover],
  );

  const handleFlowInit = useCallback((instance: ReactFlowInstance) => {
    flowRef.current = instance;
  }, []);

  return (
    <GraphFiltersProvider>
      <FlowGraphBase
        ref={graphWrapperRef}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={handleNodeClick}
        onPaneClick={handleClosePopover}
        onNodeDrag={handleNodeDrag}
        onGetNodeColor={onGetNodeColor}
        backgroundColor={GRAPH_COLORS.background}
        minZoom={0.5}
        maxZoom={7}
        width={width}
        height={height}
        onFlowInit={handleFlowInit}
      >
        <Box
          data-testid="graph-filters"
          position="absolute"
          top="3"
          right="3"
          zIndex={1}
        >
          <GraphFilters />
        </Box>
        {selectedNode && (
          <NodeDetailsPopover
            isOpen
            badgeText={
              selectedNode.data.type.charAt(0).toUpperCase() +
              selectedNode.data.type.slice(1).toLowerCase()
            }
            filePath={selectedNode.data.filePath}
            startLine={selectedNode.data.startLine}
            endLine={selectedNode.data.endLine}
            repositoryInfo={selectedNode.data.repositoryInfo}
            technologies={selectedNode.data.technologies}
            onClose={handleClosePopover}
            ref={nodeDetailsPopoverRef}
          />
        )}
      </FlowGraphBase>
    </GraphFiltersProvider>
  );
};
